import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import WebApp from "@twa-dev/sdk";
import { API_URL } from "../config";
import { useUser } from "../context/UserContext";
import { FaGem } from "react-icons/fa";
import { useTitle } from "../hooks/useTitle";
import haptic from "../utils/haptic";
import confetti from "canvas-confetti";
import { toast } from "react-toastify";

function CoinFlipGame() {
  useTitle("Coin Flip");
  const navigate = useNavigate();
  const { userData, setUserData, APIKey } = useUser();
  const [gameState, setGameState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipResult, setFlipResult] = useState(null); // 'win' or 'lose'
  const [landedOn, setLandedOn] = useState(null); // 'heads' or 'tails'

  useEffect(() => {
    fetchGameState();
  }, []);

  const fetchGameState = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/game/coinflip/state`, {
        params: { userId: userData.userId, initData: WebApp.initData },
        headers: { "x-api-key": APIKey }
      });
      setGameState(res.data.coinFlip);
    } catch (err) {
      console.error("Failed to fetch state:", err);
      toast.error("Failed to load game state.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStart = async (betAmount) => {
    if (userData.user.points < betAmount) {
      toast.error("Not enough Gems!");
      haptic.error();
      return;
    }
    haptic.tap();
    setIsLoading(true);
    setFlipResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/game/coinflip/start`, {
        userId: userData.userId,
        betAmount,
        initData: WebApp.initData
      }, { headers: { "x-api-key": APIKey } });
      
      setGameState(res.data.coinFlip);
      setUserData(prev => ({
        ...prev,
        user: { ...prev.user, points: res.data.user.points }
      }));
    } catch (err) {
      toast.error(err.response?.data?.error || "Error starting game");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFlip = async (choice) => {
    haptic.tap();
    setIsFlipping(true);
    setFlipResult(null);
    setLandedOn(null);

    try {
        // Spin animation is 2s — wait the same so result reveals exactly when coin stops
        const [res] = await Promise.all([
          axios.post(`${API_URL}/api/game/coinflip/flip`, {
            userId: userData.userId,
            choice: choice,
            initData: WebApp.initData
          }, { headers: { "x-api-key": APIKey } }),
          new Promise(r => setTimeout(r, 2000))
        ]);

      setFlipResult(res.data.result);
      setLandedOn(res.data.landedOn);
      
      if (res.data.result === "win") {
        haptic.success();
        confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 } });
        setGameState(res.data.coinFlip);
        setUserData(prev => ({
          ...prev,
          user: { ...prev.user, points: res.data.user.points }
        }));
      } else {
        haptic.error();
        // DELIBERATELY DO NOT reset gameState here!
        // This preserves the UI values (Current Pot, Round) so the user sees exactly what they lost.
        // It will be reset when they click the "Continue" button.
      }

    } catch (err) {
      toast.error("Error flipping coin.");
    } finally {
      setIsFlipping(false);
    }
  };

  const handleCashOut = async () => {
    haptic.tap();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/game/coinflip/cashout`, {
        userId: userData.userId,
        initData: WebApp.initData
      }, { headers: { "x-api-key": APIKey } });

      toast.success(`Cashed out ${res.data.cashedOutAmount} Gems!`);
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      
      setGameState(res.data.coinFlip);
      setUserData(prev => ({
        ...prev,
        user: { ...prev.user, points: res.data.user.points }
      }));
    } catch (err) {
      toast.error(err.response?.data?.error || "Error cashing out");
    } finally {
      setIsLoading(false);
    }
  };

  // Finite spin: 2 full rotations (720°) over 2s with easeOut
  // Coin decelerates naturally and stops exactly when the result arrives
  const spinTransition = {
    duration: 2,
    ease: [0.33, 1, 0.68, 1], // cubic-bezier easeOut
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-slate-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-black text-slate-900 dark:text-white min-h-screen pb-24 pt-20 transition-colors duration-300"
    >
      <div className="container mx-auto px-4 mt-2">
        <div className="flex flex-col items-center">



          {!gameState || isLoading ? (
            <div className="animate-pulse flex space-x-4">
              <div className="rounded-full bg-slate-300 dark:bg-slate-700 h-24 w-24"></div>
            </div>
          ) : !gameState.isActive ? (
             <motion.div 
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               className="w-full max-w-sm"
             >
                <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-200 dark:border-slate-700 text-center">
                  <h3 className="text-lg font-bold text-slate-700 dark:text-slate-200 mb-4 uppercase tracking-wider">Choose Initial Bet</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[10, 20, 50].map(amt => (
                      <button
                        key={amt}
                        onClick={() => handleStart(amt)}
                        disabled={userData.user?.points < amt}
                        className={`py-4 rounded-2xl font-black flex flex-col items-center justify-center gap-1 transition-all ${
                          userData.user?.points >= amt 
                          ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-orange-500/30 hover:-translate-y-1 active:scale-95" 
                          : "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                        }`}
                      >
                       <span className="text-xl">{amt}</span>
                       <FaGem size={12} className="opacity-80" />
                      </button>
                    ))}
                  </div>
                </div>
             </motion.div>
          ) : (
            <motion.div 
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               className="w-full max-w-sm flex flex-col items-center"
             >
                {/* Header (Round & Bet) */}
                <div className="w-full flex justify-between items-center mb-6 px-2">
                  <span className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest bg-white/50 dark:bg-slate-800/50 px-3 py-1 rounded-full">Round {gameState.round}</span>
                  <span className="text-xs text-slate-400 font-medium">Bet: {gameState.initialBet} <FaGem className="inline" size={10}/></span>
                </div>

                {/* Pot Section */}
                <div className="mb-6 flex flex-col items-center">
                  <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-1">Current Pot</p>
                  <div className="text-6xl font-black text-amber-500 flex items-center justify-center gap-2 drop-shadow-lg">
                    {gameState.pot} <FaGem size={36}/>
                  </div>
                </div>

                {/* Coin Animation Area */}
                <div className="relative mb-10 w-full flex justify-center">
                  <motion.div
                    animate={isFlipping ? { rotateY: [0, 720] } : { rotateY: 0 }}
                    transition={isFlipping ? spinTransition : { duration: 0 }}
                    className={`w-40 h-40 rounded-full bg-gradient-to-br shadow-[0_0_40px_rgba(245,158,11,0.5)] border-4 flex items-center justify-center relative overflow-hidden ${
                      !isFlipping && landedOn === "tails" 
                      ? "from-slate-300 to-slate-500 border-slate-200 shadow-slate-500/50" 
                      : "from-yellow-300 to-amber-600 border-yellow-200"
                    }`}
                  >
                    <div className="absolute inset-0 bg-white/20 rounded-full blur-md"></div>
                    {!isFlipping && landedOn ? (
                       <span className="text-white font-black text-3xl drop-shadow-lg uppercase tracking-widest relative z-10">{landedOn}</span>
                    ) : (
                       <FaGem className="text-white drop-shadow-lg opacity-90 relative z-10" size={60} />
                    )}
                  </motion.div>
                </div>

                {/* Controls Area */}
                <div className="w-full">
                  {flipResult === "lose" ? (
                    <div className="flex flex-col gap-3">
                      <div className="text-red-500 font-bold bg-red-100 dark:bg-red-500/20 py-3 rounded-xl mb-2 border border-red-200 dark:border-red-500/30 text-center">
                        Ouch! The coin landed on {landedOn?.toUpperCase()}. You lost the pot!
                      </div>
                      <button
                        onClick={() => {
                          setFlipResult(null);
                          setGameState({ isActive: false, pot: 0, round: 0, initialBet: 0 });
                        }}
                        className="w-full py-4 font-black rounded-2xl bg-slate-800 text-white shadow-lg hover:scale-[1.02] active:scale-95 transition-all"
                      >
                        Continue
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <button
                          onClick={() => handleFlip("heads")}
                          disabled={isFlipping}
                          className="py-4 font-black rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-lg shadow-amber-500/30 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:scale-100"
                        >
                          Heads
                        </button>
                        <button
                          onClick={() => handleFlip("tails")}
                          disabled={isFlipping}
                          className="py-4 font-black rounded-2xl bg-gradient-to-br from-slate-400 to-slate-600 text-white shadow-lg shadow-slate-500/30 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:scale-100"
                        >
                          Tails
                        </button>
                      </div>
                      <button
                        onClick={handleCashOut}
                        disabled={isFlipping}
                        className="w-full py-3 font-bold rounded-2xl bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white transition-colors disabled:opacity-50 mb-4"
                      >
                        Cash Out Current Pot
                      </button>
                      <p className="text-xs text-slate-400">Next flip pays <span className="text-amber-500 font-bold">{Math.floor(gameState.pot * 1.7)}</span> gems</p>
                    </div>
                  )}
                </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default CoinFlipGame;
